// src/components/DownloadDeckDialog.tsx
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

interface DownloadDeckDialogProps {
  children: React.ReactNode;
}

const DownloadDeckDialog = ({ children }: DownloadDeckDialogProps) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Programmatic download that keeps the current page in place
const downloadDeck = async () => {
  const url = "https://cdn.vegamrobotics.com/uploads/Final%20-%20Autodock.pdf";
  // const url = "https://cdn.vegamrobotics.com/uploads/Vegam+Robotic+brochure+(1).pdf"; // CDN URL

  // Create a temporary anchor element
  const a = document.createElement("a");
  a.href = url;
  // Open the link in a new tab (for viewing in some browsers)
  a.target = "_blank";
 
  // Set filename for download
  a.download = "Vegam Robotics.pdf";  // Set the desired filename for download
  // Append the anchor to the body and trigger the download
  document.body.appendChild(a);
  // Simulate click to trigger download
  a.click();
 
  // Clean up the DOM
  a.remove();
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 1) Save to backend CSV with form_type = download_deck
      const resp = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formType: "download_deck",
          formData: {
            firstName: name,
            lastName: "",
            email,
            phone: "",
            company: "",
            jobTitle: "",
            industry: "",
            message: "Deck download request",
          },
        }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({} as any));
        throw new Error(data?.error || `Failed with status ${resp.status}`);
      }

      // 2) Auto-download (no new tab)
      await downloadDeck();

      toast({
        title: "Success!",
        description: "Your details were saved and the deck is downloading.",
      });

      setName("");
      setEmail("");
      setOpen(false);
    } catch (error: any) {
      console.error("DownloadDeckDialog submit error:", error);
      toast({
        title: "Error",
        description:
          String(error?.message || error) || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Download Deck</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Enter your full name"
            />
          </div>

        <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email address"
            />
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Processing…" : "Download Deck"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadDeckDialog;
